import { css } from '@emotion/react';
import {
  type FC, type FormEvent, useCallback, useMemo, useState,
} from 'react';
import { FormFieldType } from '~/_shared/utils/form/formValidation.helpers';
import { useFormFieldValidation } from '~/_shared/utils/form/useFormFieldValidation';
import { useTranslation } from '~/_shared/utils/hooks';
import type { UserRegisterErrors } from '~/store/userData/repository/userData.repository';
import { RegisterFormEmailView } from './registerFormEmailView.component';
import { RegisterFormOptionsView } from './registerFormOptionsView.component';
import { type UserRegisterData } from './useUserRegister';

export const ITEMS_GAP = 16;

const defaultCursorStyle = css({
  cursor: 'default',
});

const centerTextStyle = css(defaultCursorStyle, {
  textAlign: 'center',
});

const headingPrimaryStyle = css(centerTextStyle, {
  fontSize: 22,
  margin: 0,
});

const headingSecondaryStyle = css(centerTextStyle, {
  fontSize: 14,
  margin: '8px 0 30px',
});

type RegisterFormProps = Readonly<{
  errorMessage?: string;
  errors?: UserRegisterErrors | null;
  isLoading: boolean;

  closeErrorMessage: () => void;
  onGoogleLogin: () => void;
  onMicrosoftLogin: () => void;
  onSubmit: (data: UserRegisterData) => void;
}>;

export const RegisterFormComponent: FC<RegisterFormProps> = (props) => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [t] = useTranslation();

  const { value: firstName, onBlur: onFirstNameBlur, onChange: onFirstNameChange, errors: firstNameErrors, resetErrors: resetFirstNameErrors } = useFormFieldValidation({
    type: FormFieldType.Required,
    requiredFieldErrorMessage: t('First name must not be empty'),
  });
  const { value: lastName, onBlur: onLastNameBlur, onChange: onLastNameChange, errors: lastNameErrors, resetErrors: resetLastNameErrors } = useFormFieldValidation({
    type: FormFieldType.Required,
    requiredFieldErrorMessage: t('Last name must not be empty'),
  });
  const { value: email, onBlur: onEmailBlur, onChange: onEmailChange, errors: emailErrors, resetErrors: resetEmailErrors, validateValue: validateEmailValue } = useFormFieldValidation({
    type: FormFieldType.Email,
    additionalErrors: props.errors?.email,
  });
  const { value: password, onBlur: onPasswordBlur, onChange: onPasswordChange, errors: passwordErrors, resetErrors: resetPasswordErrors } = useFormFieldValidation({
    type: FormFieldType.Password,
    additionalErrors: props.errors?.password,
  });

  const checkTerms = useCallback(() => {
    if (!termsAccepted) {
      setShowTermsError(true);
      return false;
    }
    return true;
  }, [termsAccepted]);

  const onShowEmailForm = useCallback(() => {
    const termsValid = checkTerms();
    const emailValidationErrors = validateEmailValue(email);

    if (termsValid && !emailValidationErrors.length) {
      setShowEmailForm(true);
    }
  }, [checkTerms, email, validateEmailValue]);

  const onBackToOptions = useCallback(() => {
    if (!email) {
      resetEmailErrors();
    }
    if (!firstName) {
      resetFirstNameErrors();
    }
    if (!lastName) {
      resetLastNameErrors();
    }
    if (!password) {
      resetPasswordErrors();
    }
    setShowEmailForm(false);
  }, [email, firstName, lastName, password, resetEmailErrors, resetFirstNameErrors, resetLastNameErrors, resetPasswordErrors]);

  const { onGoogleLogin: onGoogleLoginProp, onMicrosoftLogin: onMicrosoftLoginProp } = props;

  const onGoogleLogin = useCallback(() => {
    if (checkTerms()) {
      onGoogleLoginProp();
    }
  }, [checkTerms, onGoogleLoginProp]);

  const onMicrosoftLogin = useCallback(() => {
    if (checkTerms()) {
      onMicrosoftLoginProp();
    }
  }, [checkTerms, onMicrosoftLoginProp]);

  const isFormValid = useMemo(() =>
    [firstNameErrors, lastNameErrors, emailErrors, passwordErrors].every(e => !e.length),
  [firstNameErrors, lastNameErrors, emailErrors, passwordErrors]);

  const onFormSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!showEmailForm) {
      onShowEmailForm();
      return;
    }

    if (!termsAccepted) {
      setShowTermsError(true);
      return;
    }

    if (isFormValid) {
      props.onSubmit({
        email,
        firstName,
        lastName,
        password,
      });
    }
  };

  return (
    <form
      autoComplete="off"
      onSubmit={onFormSubmit}
    >
      <p css={headingPrimaryStyle}>{t('Start your free, no risk, 10 day trial.')}</p>
      <p css={headingSecondaryStyle}>{t('No credit card required. No surprises. Just results.')}</p>

      <>
        {showEmailForm ? (
          <RegisterFormEmailView
            email={{ value: email, errors: emailErrors, onBlur: onEmailBlur, onChange: onEmailChange }}
            firstName={{ value: firstName, errors: firstNameErrors, onBlur: onFirstNameBlur, onChange: onFirstNameChange }}
            lastName={{ value: lastName, errors: lastNameErrors, onBlur: onLastNameBlur, onChange: onLastNameChange }}
            password={{ value: password, errors: passwordErrors, onBlur: onPasswordBlur, onChange: onPasswordChange }}
            apiError={{ message: props.errorMessage, errors: props.errors, onClose: props.closeErrorMessage }}
            isFormValid={isFormValid}
            isLoading={props.isLoading}
            onBackToOptions={onBackToOptions}
          />
        ) : (
          <RegisterFormOptionsView
            email={{ value: email, errors: emailErrors, onBlur: onEmailBlur, onChange: onEmailChange }}
            isLoading={props.isLoading}
            terms={{
              accepted: termsAccepted,
              showError: showTermsError,
              onChange: (checked) => {
                setTermsAccepted(checked);
                setShowTermsError(!checked);
              },
            }}
            sso={{ onGoogleLogin, onMicrosoftLogin }}
            onShowEmailForm={onShowEmailForm}
          />
        )}
      </>
    </form>
  );
};
